/**
 * User Table Column Definitions
 * 
 * TanStack Table column definitions for Users table
 * Includes custom cell renderers for status badges, dates, and actions
 */

import { ColumnDef } from '@tanstack/react-table';
import { FiEdit2, FiEye } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { StatusBadge } from '@/components/Common/StatusBadge';
import type { UserListItem, UserStatus } from '@/types';
import styles from './userColumns.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * User Actions Props
 */
export interface UserActionsProps {
  onEdit?: (user: UserListItem) => void;
  onView?: (user: UserListItem) => void;
}

/**
 * Create user columns for TanStack Table
 */
export const createUserColumns = (actions: UserActionsProps): ColumnDef<UserListItem>[] => [
  {
    id: 'firstName',
    accessorKey: 'firstName',
    header: 'Nome',
    cell: ({ row }) => (
      <div 
        className={styles.userInfo}
        onClick={() => actions.onView?.(row.original)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.avatar}>
          {row.original.firstName.charAt(0).toUpperCase()}
        </div>
        <span className={styles.userName}>{row.original.firstName}</span>
      </div>
    ),
    enableSorting: true,
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: 'Email',
    cell: ({ getValue }) => (
      <span className={styles.emailCell}>{getValue() as string}</span>
    ),
    enableSorting: true,
  },
  {
    id: 'organizationId',
    accessorKey: 'organizationId',
    header: 'Organização',
    cell: ({ getValue }) => (
      <span className={styles.orgBadge}>
        ID: {getValue() as number}
      </span>
    ),
    enableSorting: true,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue() as UserStatus;
      return (
        <StatusBadge status={status} type="user" />
      );
    },
    enableSorting: true,
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Criado em',
    cell: ({ getValue }) => (
      <span className={styles.dateCell}>{formatDate(getValue() as string)}</span>
    ),
    enableSorting: true,
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Atualizado em',
    cell: ({ getValue }) => {
      const value = getValue() as string | undefined;
      return (
        <span className={styles.dateCell}>
          {value ? formatDate(value) : '-'}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    id: 'actions',
    header: 'Ações',
    cell: ({ row }) => {
      const user = row.original;
      
      return (
        <div className={styles.actionsWrapper}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onView?.(user)}
            title="Visualizar detalhes"
            iconLeft={<FiEye size={16} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onEdit?.(user)}
            title="Editar usuário"
            iconLeft={<FiEdit2 size={16} />}
          />
        </div>
      );
    },
    enableSorting: false,
  },
];
